import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSettings } from '../../../hooks/useAppSettings.tsx';
import moment from 'moment';
import { findByExactDateAndBranch, readPositionsByBranchAndShift } from '../../../services/branchSchedule.service.ts';
import { shifts } from '../../../utils/constanst.js';
import Layout from '../../Layout.tsx';
import Title from '../../Title.tsx';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/index.ts';
import { useWebSocket } from '../../../hooks/useWebSocket.tsx';
import { readBranches } from '../../../services/branch.service.ts';
import Branch from '../../../models/Branch.ts';
import CustomSelectField from '../../formik/CustomSelectField.tsx';
import { useFormik } from 'formik';

const groupPositionsByUniqueName = (positions) => {
  const groupedPositions = {};

  positions.forEach((position) => {
    const positionName = position.position.name;

    if (!groupedPositions[positionName]) {
      groupedPositions[positionName] = [];
    }

    const totalWorkersRequired = position.numberOfWorkers;
    for (let i = 0; i < totalWorkersRequired; i++) {
      const workerKey = `${positionName} ${i + 1}`;
      const workerIndex = groupedPositions[positionName].findIndex(
        (worker) => worker.positionName === workerKey
      );

      if (workerIndex === -1) {
        groupedPositions[positionName].push({
          positionName: workerKey,
          shifts: [position.shift],
        });
      } else {
        groupedPositions[positionName][workerIndex].shifts.push(position.shift);
      }
    }
  });

  return Object.values(groupedPositions).flat();
};

// Solo 5 estados como ScheduleCompact: Asistencia, Retardo, Sin asistencia, Puesto sin cubrir, No aplica
const COBERTURA_THEME = {
  success: {
    bg: '#d1fae5',
    border: '#10b981',
    text: '#065f46',
    dot: '#059669',
  },
  late: {
    bg: '#fde68a',
    border: '#f59e0b',
    text: '#92400e',
    dot: '#d97706',
  },
  danger: {
    bg: '#fecaca',
    border: '#ef4444',
    text: '#991b1b',
    dot: '#dc2626',
  },
  uncovered: {
    bg: '#e2e8f0',
    border: '#94a3b8',
    text: '#475569',
    dot: '#64748b',
  },
  na: {
    bg: '#cbd5e1',
    border: '#94a3b8',
    text: '#475569',
    dot: '#64748b',
  },
} as const;

const ResumenHorariosAsistencia = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { id: ipParams } = useParams();

  useAppSettings();
  const [schedules, setSchedules] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [branchs, setBranchs] = useState<any[]>([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      branch: -99,
    },
    onSubmit: () => {
      fetchDependencies();
    },
  });

  const fetchBranches = async () => {
    try {
      const branches = await readBranches();
      setBranchs(branches);
      
      const defaultBranchId = user?.rol === "Lider de sucursal" 
        ? Number(user.branchId) 
        : Number(ipParams);
      
      const branch = branches.find(
        (branch: Branch) => Number(branch.id) === defaultBranchId
      );
      
      if (branch) {
        await formik.setFieldValue("branch", branch.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDependencies = useCallback(async () => {
    const id = formik.values.branch !== -99 ? Number(formik.values.branch) : null;
    if (!id) return;

    const today = moment().format('YYYY-MM-DD');

    try {
      const fetchedSchedules = await findByExactDateAndBranch(today, id);
      setSchedules(fetchedSchedules);

      const fetchedPositions = await readPositionsByBranchAndShift(id, 'Todos');
      const uniquePositionArray = groupPositionsByUniqueName(fetchedPositions);
      setPositions(uniquePositionArray);
    } catch (error) {
      console.error('Error fetching data: ', error);
    }
  }, [formik.values.branch]);

  useEffect(() => {
    fetchBranches();
  }, [user]);

  useEffect(() => {
    if (formik.values.branch !== -99) {
      fetchDependencies();
    }
  }, [fetchDependencies]);


  const getStatusKey = (partners: any[], position: any, shiftName: string): keyof typeof COBERTURA_THEME => {
    if (!position.shifts.includes(shiftName)) return 'na';
    if (partners.length === 0) return 'uncovered';

    const hasAttendance = (partner: any) => !!partner?.assistanceCheck;
    let missingCounter = 0;
    partners.forEach((p) => { if (!hasAttendance(p)) missingCounter++; });

    if (missingCounter === partners.length) return 'danger';
    if (partners.length === 1) {
      const p = partners[0];
      if (!hasAttendance(p)) return 'danger';
      if (p?.assistanceCheck?.status === 'Asistencia') return 'success';
      if (p?.assistanceCheck?.status === 'Retardo' || p?.assistanceCheck?.status === 'Fuera de horario') return 'late';
      return 'success';
    }
    return missingCounter > 0 ? 'uncovered' : 'success';
  };

  const { isConnected, assistancesObserver } = useWebSocket();

  useEffect(() => {
    if (isConnected && formik.values.branch !== -99) {
      const unsubscribe = assistancesObserver((data) => {
        const { branchId } = data;

        if (branchId === Number(formik.values.branch)) {
          fetchDependencies();
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [isConnected, formik.values.branch]);

  const legendItems: { key: keyof typeof COBERTURA_THEME; label: string }[] = [
    { key: 'success', label: 'Asistencia' },
    { key: 'late', label: 'Retardo' },
    { key: 'danger', label: 'Sin asistencia' },
    { key: 'uncovered', label: 'Puesto sin cubrir' },
    { key: 'na', label: 'No aplica' },
  ];

  return (
    <Layout loading={!isConnected}>
      <Title baseTitle="Cobertura" basePath="" title="Cobertura en tiempo real" />
      <div className="tab-pane fade active show" id="Puestos">
        <div className="card border-0 m-4 p-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderRadius: '12px' }}>
          <div className="d-flex flex-row w-100 justify-content-center align-items-end gap-2">
            <div className="w-25">
              <CustomSelectField
                formik={formik}
                field="branch"
                list={branchs}
                label="Sucursal"
                disabled={user?.rol === "Lider de sucursal"}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="tab-pane fade active show" id="Puestos">
        <div className="card border-0 m-4" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderRadius: '12px', overflow: 'hidden' }}>
          <div className="tab-content p-4">
            <div className="mb-4">
              <span className="text-muted small text-uppercase me-2">Simbología</span>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {legendItems.map(({ key, label }) => {
                  const t = COBERTURA_THEME[key];
                  return (
                    <span
                      key={key}
                      className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill small"
                      style={{
                        backgroundColor: t.bg,
                        border: `1px solid ${t.border}`,
                        color: t.text,
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: t.dot }} />
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
            <div
              className="table-sticky mb-0 overflow-hidden"
              style={{ border: '1px solid #e2e8f0', borderRadius: '12px' }}
            >
              <table
                className="table table-panel text-nowrap align-middle mb-0"
                style={{ fontSize: '0.9rem', borderCollapse: 'separate', borderSpacing: 0 }}
              >
                <thead className="sticky-thead">
                  <tr style={{ backgroundColor: '#f8fafc' }}>
                    <th style={{ width: '20%', padding: '14px 16px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '12px' }}>Puesto</th>
                    <th style={{ width: '20%', padding: '14px 16px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Matutino</th>
                    <th style={{ width: '20%', padding: '14px 16px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' }}>Intermedio</th>
                    <th style={{ width: '20%', padding: '14px 16px', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', borderTopRightRadius: '12px' }}>Vespertino</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((position, index) => {
                    const isLastRow = index === positions.length - 1;
                    return (
                    <tr
                      key={index}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#fff' : '#fafbfc',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <td style={{ width: '20%', padding: '12px 16px', fontWeight: 500, color: '#334155', borderRight: '1px solid #f1f5f9', ...(isLastRow && { borderBottomLeftRadius: '12px' }) }}>
                        {position.positionName}
                      </td>
                      {shifts.filter(shift => shift.name !== 'Todos').map((shift, idx) => {
                        const partners = schedules.filter(
                          (schedule) =>
                            schedule.positionName === position.positionName &&
                            schedule.shift === shift.name
                        );
                        const statusKey = getStatusKey(partners, position, shift.name);
                        const t = COBERTURA_THEME[statusKey];
                        const isLastCol = idx === 2;
                        return (
                          <td
                            key={idx}
                            style={{
                              width: '20%',
                              padding: '12px 16px',
                              backgroundColor: t.bg,
                              borderRight: idx < 2 ? '1px solid #e2e8f0' : undefined,
                              color: t.text,
                              ...(isLastRow && isLastCol && { borderBottomRightRadius: '12px' }),
                            }}
                          >
                            {partners.length > 0
                              ? partners.map((partner, pidx) => (
                                  <div key={pidx} style={{ lineHeight: 1.4 }}>
                                    {`${partner.partner.person.firstName} ${partner.partner.person.lastName}`}
                                  </div>
                                ))
                              : null}
                          </td>
                        );
                      })}
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResumenHorariosAsistencia;
